import { ApiError } from '../../api/client';
import { startStripeCheckout, type StripeCheckoutInput } from '../../api/endpoints/stripe';
import { LockGlyph } from '../../components/PagoMethods';
import { effectiveCycle, planHasAnnualBilling } from '../../lib/plan';
import { cardStepCopy } from '../../lib/planPayment';
import { formatTrialDate, holdsFirstCharge, isTrialRunning } from '../../lib/trial';
import { useMutationAttempt } from '../../lib/use-mutation-attempt';
import type { SignupDraft } from './draft';
import { Named } from './Question';

const CARD_ERROR = 'No pudimos abrir el pago con tarjeta. Intenta de nuevo en unos minutos.';

type DoneStepProps = {
  draft: SignupDraft;
  /** Stripe is on and this new business unlocks with a card. */
  cardRequired: boolean;
  /** The business is on its free trial until this instant: the panel first, no payment step. */
  trialEndsAt?: string | null;
  /** Stripe is on, so the owner may save a card now if they want to. */
  canSaveCard?: boolean;
  /** Still reading whether a free trial applies: show the celebration, hold the next step. */
  resolving?: boolean;
  onContinue: () => void;
  /** Straight into the panel (the trial needs no payment step). */
  onEnterPanel: () => void;
};

/**
 * The business exists. One calm celebration, then the one thing left: with
 * Stripe on and a free trial, the panel, with saving the card as an option;
 * with Stripe on and no trial, the card (the exact amount first, then
 * Stripe's own secure page); without Stripe, the payment screen for a paid
 * plan or the panel.
 */
export function DoneStep({ draft, cardRequired, trialEndsAt, canSaveCard = true, resolving = false, onContinue, onEnterPanel }: DoneStepProps) {
  // Reservations only has no monthly fee, so no trial and no card: it goes straight to its first day.
  const paid = planHasAnnualBilling(draft.plan);
  const onTrial = paid && !cardRequired && isTrialRunning(trialEndsAt);
  const ready = !resolving || !paid;
  return (
    <div className="wiz-q wiz-done">
      <h1 className="wiz-title" id="listo-title">
        Listo, <Named>{draft.businessName.trim()}</Named> ya está en Dale.
      </h1>
      {!ready ? <p className="wiz-hint">Preparando tu panel…</p> : null}
      {ready && onTrial && trialEndsAt ? <TrialAction draft={draft} trialEndsAt={trialEndsAt} canSaveCard={canSaveCard} onEnterPanel={onEnterPanel} /> : null}
      {ready && !onTrial && paid && cardRequired ? <CardAction draft={draft} /> : null}
      {ready && !onTrial && !(paid && cardRequired) ? <ContinueAction draft={draft} onContinue={onContinue} /> : null}
    </div>
  );
}

function ContinueAction({ draft, onContinue }: { draft: SignupDraft; onContinue: () => void }) {
  const paid = planHasAnnualBilling(draft.plan);
  return (
    <>
      <p className="wiz-hint">
        {paid
          ? 'Solo falta tu pago para activar tu panel. Es un paso seguro y lo haces una sola vez.'
          : 'Tu panel ya está listo. Sube tus fotos y tus servicios, y sales en la app de Dale.'}
      </p>
      <div className="wiz-actions">
        <button type="button" className="btn wiz-next" onClick={onContinue} autoFocus>
          {paid ? 'Activar con mi tarjeta' : 'Preparar mi ficha'}
        </button>
      </div>
    </>
  );
}

function useCardCheckout() {
  return useMutationAttempt({
    mutationFn: (input: StripeCheckoutInput, key: string) => startStripeCheckout(input, key),
  });
}

function CardError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className="wiz-error" role="alert">
      {error instanceof ApiError ? error.message : CARD_ERROR}
    </p>
  );
}

type TrialActionProps = { draft: SignupDraft; trialEndsAt: string; canSaveCard: boolean; onEnterPanel: () => void };

/** The free trial: the panel first, the card only if the owner wants it now. */
function TrialAction({ draft, trialEndsAt, canSaveCard, onEnterPanel }: TrialActionProps) {
  const start = useCardCheckout();
  const until = formatTrialDate(trialEndsAt);
  const heldUntilEnd = holdsFirstCharge(trialEndsAt);
  return (
    <>
      <p className="wiz-hint">
        <strong className="wiz-trial">Gratis hasta el {until}.</strong> Usa tu panel completo desde hoy, sin tarjeta.
      </p>
      <CardError error={start.error} />
      <div className="wiz-actions">
        <button type="button" className="btn wiz-next" onClick={onEnterPanel} autoFocus>
          Entrar a mi panel
        </button>
        {canSaveCard ? (
          <button
            type="button"
            className="btn secondary wiz-next"
            disabled={start.isPending}
            onClick={() => start.mutate({ plan: draft.plan, cycle: effectiveCycle(draft.plan, draft.cycle) })}
          >
            {start.isPending ? 'Abriendo Stripe…' : 'Guardar tarjeta'}
          </button>
        ) : null}
      </div>
      {canSaveCard ? (
        <p className="wiz-trust">
          <LockGlyph />
          {heldUntilEnd
            ? `Si la guardas ahora, no te cobramos nada hasta el ${until}. Dale no ve ni guarda los datos de tu tarjeta.`
            : 'Guardas tu tarjeta en la página segura de Stripe. Dale no ve ni guarda sus datos.'}
        </p>
      ) : null}
    </>
  );
}

function CardAction({ draft }: { draft: SignupDraft }) {
  const start = useCardCheckout();
  const copy = cardStepCopy({ plan: draft.plan, cycle: draft.cycle });
  return (
    <>
      <p className="wiz-hint">{copy.lede}</p>
      <CardError error={start.error} />
      <div className="wiz-actions">
        <button
          type="button"
          className="btn wiz-next"
          autoFocus
          disabled={start.isPending}
          onClick={() => start.mutate({ plan: draft.plan, cycle: effectiveCycle(draft.plan, draft.cycle) })}
        >
          {start.isPending ? 'Abriendo Stripe…' : copy.action}
        </button>
      </div>
      <p className="wiz-trust">
        <LockGlyph />
        Pagas en la página segura de Stripe. Dale no ve ni guarda los datos de tu tarjeta.
      </p>
    </>
  );
}
